import {merge} from 'rxjs';
import {filter, map, withLatestFrom} from 'rxjs/operators';
import {toWsMsg$, wsMsg$} from '@/ws';
import {track} from '@/et';
import {aopUserIsMatched} from '@/raw/utils';
import {mainUserId$} from '@/raw/users';
import {genAopParams} from '@/raw/aop-all';
import {wsLogin$} from '@/user/auth-ws';
import type {ws} from '@/types';
type AOPMsg = ws.AOPMsg;
type AOPMsgV2 = ws.AOPMsgV2;

let subTime = 0;

wsLogin$
  .pipe(
    withLatestFrom(mainUserId$),
    filter(([, userId]) => !!userId)
  )
  .subscribe(([, id]) => {
    subTime = Date.now();
    const params = genAopParams(id, true);
    toWsMsg$.next({method: 'aop_v1.subscribe', params});
    toWsMsg$.next({method: 'aop_p1.subscribe', params});
  });

export const aopMsg$ = wsMsg$.pipe(
  filter((o: AOPMsg) => !!o.accounts && !!o.orders && !!o.positions),
  withLatestFrom(mainUserId$),
  filter(([o, mainUserId]) => !mainUserId || aopUserIsMatched(o, mainUserId)),
  map(([o]) => {
    o.accounts = ensureTradeLevel(o.accounts);
    o.positions = addTradeLevel(o.positions, o.accounts);
    return o;
  })
);

export const aopMsgV2$ = wsMsg$.pipe(
  filter((o: AOPMsgV2) => !!o.accounts_p1 && !!o.orders_p1 && !!o.positions_p1),
  withLatestFrom(mainUserId$),
  filter(([o, mainUserId]) => !mainUserId || aopUserIsMatched(o, mainUserId)),
  map(([{accounts_p1, orders_p1, positions_p1, events_p1, ...data}]) => {
    const accounts = ensureTradeLevel(accounts_p1);
    return {
      ...data,
      accounts,
      orders: orders_p1,
      positions: addTradeLevel(positions_p1, accounts),
      events: events_p1,
    };
  })
);

function ensureTradeLevel<T extends ws.ContractAccountRaw | ws.ContractAccountRawV2>(accounts: T[]): T[] {
  accounts.forEach((account) => {
    account.tradeLevel = account.tradeLevel || 0;
  });
  return accounts;
}

function addTradeLevel(
  positions: ws.ContractPositionRaw[],
  accounts: (ws.ContractAccountRaw | ws.ContractAccountRawV2)[]
) {
  return positions.map((position) => {
    const account = accounts.find((o) => o.accountID === position.accountID);
    if (account) {
      position.tradeLevel = account.tradeLevel;
    }
    return position;
  });
}

merge(aopMsg$, aopMsgV2$).subscribe(posAopDebugMsg);

function posAopDebugMsg(aop) {
  const {accounts, positions, sequence, timestamp, type, version, guid = ''} = aop;
  const as = accounts.map(formatTrackAccount);
  const ps = positions.filter((o) => o.size > 0).map(formatTrackPosition);
  const os = (aop.orders.open || []).map(formatTrackOrder);
  const ocs = (type === 'incremental' ? aop.orders.closed || [] : []).map(formatTrackOrder);
  const ofs = (type === 'incremental' ? aop.orders.fills || [] : []).map(formatTrackOrder);
  const delayV1 = Date.now() - subTime;
  const delayP1 = Date.now() - subTime;
  track('aop_debug', {guid, as, ps, os, ocs, ofs, sequence, timestamp, type, version, subTime, delayV1, delayP1});
}

function formatTrackAccount(account) {
  const {
    currency,
    accountID,
    accountBalanceRv,
    accountBalanceEv,
    bonusBalanceRv,
    bonusBalanceEv,
    totalUsedBalanceRv,
    totalUsedBalanceEv,
    unrealisedPnlRv,
    unrealisedPnlEv,
    tradeLevel,
  } = account;

  if (['USDT', 'PT'].includes(currency)) {
    return {
      currency,
      accountID,
      accountBalanceRv,
      bonusBalanceRv,
      totalUsedBalanceRv,
      unrealisedPnlRv,
      tradeLevel,
    };
  }
  return {
    currency,
    accountID,
    accountBalanceEv,
    bonusBalanceEv,
    totalUsedBalanceEv,
    unrealisedPnlEv,
    tradeLevel,
  };
}
function formatTrackPosition(position) {
  const base = {
    symbol: position.symbol,
    size: position.size,
    currency: position.currency,
    freeQty: position.freeQty,
  };
  const isV2 = !!position.leverageRr;
  return Object.assign(
    base,
    isV2
      ? {
          leverageRr: position.leverageRr,
          unrealisedPnlRv: position.unrealisedPnlRv,
          liquidationPriceRp: position.liquidationPriceRp,
          assignedPosBalanceRv: position.assignedPosBalanceRv,
        }
      : {
          leverageEr: position.leverageEr,
          unrealisedPnlEv: position.unrealisedPnlEv,
          liquidationPriceEp: position.liquidationPriceEp,
          assignedPosBalanceEv: position.assignedPosBalanceEv,
        }
  );
}
function formatTrackOrder(order) {
  return {
    symbol: order.symbol,
    action: order.action,
    orderID: order.orderID,
    cumQty: order.cumQty,
    orderQty: order.orderQty,
    curPosSide: order.curPosSide,
    ordStatus: order.ordStatus,
    ordType: order.ordType,
  };
}
